
import { useState, useRef, useCallback, useEffect } from 'react';

interface TouchPoint {
  x: number;
  y: number;
  timestamp: number;
}

interface MobileColumnTouchOptions {
  onColumnSwipe?: (direction: 'left' | 'right') => void;
  currentColumnIndex: number;
  totalColumns: number;
  swipeThreshold?: number;
  velocityThreshold?: number;
  edgeWidth?: number;
}

export const useMobileColumnTouch = (options: MobileColumnTouchOptions) => {
  const {
    onColumnSwipe,
    currentColumnIndex,
    totalColumns,
    swipeThreshold = 50,
    velocityThreshold = 0.3,
    edgeWidth = 32
  } = options;

  const [isActive, setIsActive] = useState(false);
  const startTouchRef = useRef<TouchPoint | null>(null);
  const lastTouchRef = useRef<TouchPoint | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const edgeZoneRef = useRef<'left' | 'right' | null>(null);

  const isInEdgeZone = useCallback((clientX: number): 'left' | 'right' | null => {
    if (!containerRef.current) return null;
    
    const rect = containerRef.current.getBoundingClientRect();
    
    if (clientX <= rect.left + edgeWidth) {
      return 'left';
    } else if (clientX >= rect.right - edgeWidth) {
      return 'right';
    }
    
    return null;
  }, [edgeWidth]);

  const shouldIgnoreTouch = useCallback((target: Element): boolean => {
    // Ignore touches on draggable elements and interactive components
    const ignoreSelectors = [
      '[data-rbd-draggable-id]',
      '[data-rbd-drag-handle-draggable-id]',
      'button',
      'input',
      'textarea',
      'select',
      'a',
      '[role="button"]',
      '.dropdown-menu',
      '.context-menu'
    ];
    
    return ignoreSelectors.some(selector => target.closest(selector));
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const target = e.target as Element;
    
    // Ignore touches on interactive elements or DnD elements
    if (shouldIgnoreTouch(target)) {
      return;
    }

    const touch = e.touches[0];
    const edgeZone = isInEdgeZone(touch.clientX);
    
    // Only handle touches in edge zones for swiping
    if (!edgeZone) {
      return;
    }

    const touchPoint: TouchPoint = {
      x: touch.clientX,
      y: touch.clientY,
      timestamp: Date.now()
    };

    startTouchRef.current = touchPoint;
    lastTouchRef.current = touchPoint;
    edgeZoneRef.current = edgeZone;
    setIsActive(true);
  }, [shouldIgnoreTouch, isInEdgeZone]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isActive || !startTouchRef.current || !edgeZoneRef.current) return;

    const touch = e.touches[0];
    const currentTouch: TouchPoint = {
      x: touch.clientX,
      y: touch.clientY,
      timestamp: Date.now()
    };

    const deltaX = currentTouch.x - startTouchRef.current.x;
    const deltaY = Math.abs(currentTouch.y - startTouchRef.current.y);
    
    // If vertical movement is too large, this is probably a scroll gesture - cancel
    if (deltaY > 30) {
      setIsActive(false);
      startTouchRef.current = null;
      lastTouchRef.current = null;
      edgeZoneRef.current = null;
      return;
    }

    // Only prevent default for significant horizontal movements in edge zones
    // CSS touch-action should handle most of this, but this is a backup
    if (Math.abs(deltaX) > 20) {
      e.preventDefault();
    }

    lastTouchRef.current = currentTouch;
  }, [isActive]);

  const handleTouchEnd = useCallback(() => {
    if (!isActive || !startTouchRef.current || !lastTouchRef.current || !edgeZoneRef.current) {
      // Reset state
      setIsActive(false);
      startTouchRef.current = null;
      lastTouchRef.current = null;
      edgeZoneRef.current = null;
      return;
    }

    const deltaX = lastTouchRef.current.x - startTouchRef.current.x;
    const deltaTime = lastTouchRef.current.timestamp - startTouchRef.current.timestamp;
    const velocity = deltaTime > 0 ? Math.abs(deltaX) / deltaTime : 0;

    // Determine swipe direction and validate
    if (Math.abs(deltaX) > swipeThreshold || velocity > velocityThreshold) {
      if (deltaX > 0 && currentColumnIndex > 0) {
        // Swipe right - go to previous column
        onColumnSwipe?.('right');
      } else if (deltaX < 0 && currentColumnIndex < totalColumns - 1) {
        // Swipe left - go to next column
        onColumnSwipe?.('left');
      }
    }

    // Reset state
    setIsActive(false);
    startTouchRef.current = null;
    lastTouchRef.current = null;
    edgeZoneRef.current = null;
  }, [isActive, onColumnSwipe, currentColumnIndex, totalColumns, swipeThreshold, velocityThreshold]);

  // Attach event listeners to container - use passive listeners where possible
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Use passive listeners for start and end, non-passive for move to allow preventDefault when needed
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    containerRef,
    isActive
  };
};
